import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import OTPInput from "@/components/ui/otp-input";
import { MFAMessages } from "./MFAMessages";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Label } from "@radix-ui/react-label";
import { t } from "i18next";
import { SignHeader } from "@/components/ui/SignHeader";

const otpLength = 6;
const FormSchema = z.object({
  otp: z
    .string()
    .min(
      otpLength,
      t("login.twoFactorForm.error.length", { length: otpLength })
    )
    .max(
      otpLength,
      t("login.twoFactorForm.error.length", { length: otpLength })
    )
    .regex(/^\d+$/, t("login.twoFactorForm.error.numbers")),
});

type FormData = z.infer<typeof FormSchema>;

interface TwoFactorFormProps {
  error: string | null;
  loading: boolean;
  onSubmit: (code: string) => void;
}

const TwoFactorForm = (props: TwoFactorFormProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      otp: "",
    },
  });

  const onSubmit = (data: FormData): void => {
    props.onSubmit(data.otp);
  };

  return (
    <div className="flex flex-col items-center justify-center w-screen h-screen bg-background px-4">
      <div id="recaptcha-container"></div>
      <SignHeader title={t("login.twoFactorForm.formTitle")} />
      <div className="w-full max-w-sm space-y-4 mt-4">
        <MFAMessages errorId={props.error} />
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col items-start space-y-2">
            <Label>{t("login.twoFactorForm.labelSmsCode")}</Label>
            <Controller
              control={control}
              name="otp"
              render={({ field }) => (
                <OTPInput length={otpLength} onChangeOTP={field.onChange} />
              )}
            />
            {errors.otp && (
              <p className="text-error text-sm text-center">
                {errors.otp.message}
              </p>
            )}
          </div>

          <div className="flex justify-center">
            <Button
              className="w-full"
              data-testid="login-submit-button"
              disabled={props.loading}
              size="xl"
              type="submit"
            >
              {props.loading
                ? t("login.form.loadingSignInButton")
                : t("login.form.signInButton")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorForm;
